/* PastéBCN — Hero slider
   Web Component <pb-hero-slider> con:
   - Autoplay configurable (pausa hover, focus-within, tab oculta, reduced-motion)
   - Transiciones fade o slide
   - Indicadores, flechas, pause/play, swipe táctil, teclado (← →)
   - aria-current, aria-hidden, anuncio polite a screen readers
   Solo se inicializa la rotación si hay 2+ slides. */

if (!customElements.get('pb-hero-slider')) {
  class PbHeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.pb-hero__slide'));
      if (!this.slides.length) return;

      this.indicators = Array.from(this.querySelectorAll('.pb-hero__indicator'));
      this.prevBtn = this.querySelector('.pb-hero__arrow--prev');
      this.nextBtn = this.querySelector('.pb-hero__arrow--next');
      this.pauseBtn = this.querySelector('.pb-hero__pause');
      this.track = this.querySelector('.pb-hero__track');
      this.live = this.querySelector('.pb-hero__live');

      this.index = 0;
      this.total = this.slides.length;
      this.isPlaying = false;
      this.userPaused = false;
      this.timer = null;
      this.cleanups = [];

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 6) * 1000;
      this.transitionType = this.dataset.transitionType || 'fade';
      this.swipeEnabled = this.dataset.swipe !== 'false';

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.reducedMotion) this.autoplayEnabled = false;

      this._setActive(0, false);

      if (this.total <= 1) return;

      this._bindControls();
      this._bindAutoplayPauses();
      this._bindKeyboard();
      if (this.swipeEnabled) this._bindSwipe();

      if (this.autoplayEnabled) this.play();
    }

    disconnectedCallback() {
      this._stopTimer();
      this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
    }

    _bindControls() {
      if (this.prevBtn) {
        const handler = () => this.prev(true);
        this.prevBtn.addEventListener('click', handler);
        this.cleanups.push(() => this.prevBtn.removeEventListener('click', handler));
      }
      if (this.nextBtn) {
        const handler = () => this.next(true);
        this.nextBtn.addEventListener('click', handler);
        this.cleanups.push(() => this.nextBtn.removeEventListener('click', handler));
      }
      this.indicators.forEach((indicator, idx) => {
        const handler = () => this.goTo(idx, true);
        indicator.addEventListener('click', handler);
        this.cleanups.push(() => indicator.removeEventListener('click', handler));
      });
      if (this.pauseBtn) {
        const handler = () => this.togglePlay();
        this.pauseBtn.addEventListener('click', handler);
        this.cleanups.push(() => this.pauseBtn.removeEventListener('click', handler));
      }
    }

    _bindAutoplayPauses() {
      const enter = () => {
        if (this.isPlaying) {
          this._wasPlaying = true;
          this._stopTimer();
        }
      };
      const leave = () => {
        if (this._wasPlaying && !this.userPaused) {
          this._wasPlaying = false;
          this._restartTimer();
        }
      };
      this.addEventListener('mouseenter', enter);
      this.addEventListener('mouseleave', leave);
      this.addEventListener('focusin', enter);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) leave();
      });

      const visHandler = () => {
        if (document.hidden) this._stopTimer();
        else if (this.isPlaying && !this.userPaused) this._restartTimer();
      };
      document.addEventListener('visibilitychange', visHandler);
      this.cleanups.push(() => document.removeEventListener('visibilitychange', visHandler));
    }

    _bindKeyboard() {
      const handler = (e) => {
        if (!this.contains(document.activeElement)) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev(true);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next(true);
        }
      };
      this.addEventListener('keydown', handler);
      this.cleanups.push(() => this.removeEventListener('keydown', handler));
    }

    _bindSwipe() {
      let startX = 0;
      let startY = 0;
      const onStart = (e) => {
        startX = e.changedTouches[0].screenX;
        startY = e.changedTouches[0].screenY;
      };
      const onEnd = (e) => {
        const dx = e.changedTouches[0].screenX - startX;
        const dy = e.changedTouches[0].screenY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx > 0) this.prev(true);
          else this.next(true);
        }
      };
      this.addEventListener('touchstart', onStart, { passive: true });
      this.addEventListener('touchend', onEnd, { passive: true });
      this.cleanups.push(() => this.removeEventListener('touchstart', onStart));
      this.cleanups.push(() => this.removeEventListener('touchend', onEnd));
    }

    play() {
      if (this.total <= 1 || this.reducedMotion) return;
      this.userPaused = false;
      this.isPlaying = true;
      this.dataset.state = 'playing';
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', 'false');
        this.pauseBtn.setAttribute('aria-label', 'Pausar carrusel');
      }
      this._restartTimer();
    }

    pause(byUser = false) {
      if (byUser) {
        this.userPaused = true;
        this.isPlaying = false;
        this.dataset.state = 'paused';
        if (this.pauseBtn) {
          this.pauseBtn.setAttribute('aria-pressed', 'true');
          this.pauseBtn.setAttribute('aria-label', 'Reproducir carrusel');
        }
      }
      this._stopTimer();
    }

    togglePlay() {
      if (this.isPlaying) this.pause(true);
      else this.play();
    }

    _stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    _restartTimer() {
      this._stopTimer();
      if (!this.isPlaying || this.total <= 1) return;
      this.timer = setTimeout(() => this.next(false), this.autoplaySpeed);
    }

    next(announce) {
      this.goTo((this.index + 1) % this.total, announce);
    }

    prev(announce) {
      this.goTo((this.index - 1 + this.total) % this.total, announce);
    }

    goTo(target, announce) {
      if (target === this.index || target < 0 || target >= this.total) return;
      this._setActive(target, true);
      if (announce && this.live) {
        this.live.textContent = `Slide ${target + 1} de ${this.total}`;
      }
      if (this.isPlaying) this._restartTimer();
    }

    _setActive(target, animate) {
      const prev = this.slides[this.index];
      const next = this.slides[target];

      if (prev) {
        prev.classList.remove('is-active');
        prev.setAttribute('aria-hidden', 'true');
        prev.removeAttribute('aria-current');
      }
      if (next) {
        next.classList.add('is-active');
        next.setAttribute('aria-hidden', 'false');
        next.setAttribute('aria-current', 'true');
      }

      if (this.indicators[this.index]) {
        this.indicators[this.index].setAttribute('aria-current', 'false');
      }
      if (this.indicators[target]) {
        this.indicators[target].setAttribute('aria-current', 'true');
      }

      if (this.transitionType === 'slide' && this.track) {
        this.track.style.transform = `translateX(-${target * 100}%)`;
      }

      this.index = target;
    }
  }

  customElements.define('pb-hero-slider', PbHeroSlider);
}
